/// <amd-module name="Wit/SprintsHub/SprintsView/Services/TaskboardService" />
import { IBacklogConfiguration } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { IIterationData } from "Agile/Contracts/Common";
import { getTFSProject } from "Tfs/Platform/Page";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals } from "VSS/Core/Util/String";
import { IVssPageContext, Services } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Filter, IFilter } from "VSSUI/Utilities/Filter";
import {
    IClientWorkItem,
    IObservableClientWorkItem,
    ObservableClientWorkItem,
    ParentField,
    WorkItemEvents
} from "Wit/ClientWorkItemService/IClientWorkItem";
import { ClientWorkItemService, ClientWorkItemServiceEvent } from "Wit/ClientWorkItemService/IClientWorkItemService";
import {
    IChangeIterationWorkItemData,
    ICommonBacklogContentService,
    ICurrentIterationData
} from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { BoardFilterDataSource } from "Wit/Common/Controls/Boards/BoardFilterDataSource";
import { CoreFieldRefNames, OobFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IPlanningPaneViewService } from "Wit/Common/PlanningPane/Services/PlanningPaneViewService";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { IWorkItemFormDialogService } from "Wit/Common/WorkItemDialogs/Services/IWorkItemFormDialogService";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { BaseIntComparer, CompositeComparer, SortDirection } from "Wit/Common/WorkItemLinksService/CompositeComparer";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { FilterManager } from "Wit/Filter/FilterManager";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import { convertWorkItemIdentityRefFromFieldValue, getDistinctDisplayName } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { ParentChangeType, TaskboardGroupBy, WorkItemSignalREventType, unassignedId, unparentedId } from "Wit/SprintsHub/Common/Constants";
import {
    ISprintTaskboardData,
    ISprintTaskboardErrorData,
    ITaskboardModelOptions,
    ITaskboardWorkItem,
    ITaskboardWorkItemChangedPayload,
    ITaskboardWorkItemColumnChangedPayload
} from "Wit/SprintsHub/Common/Contracts/Interfaces";
import { TaskboardWorkItem } from "Wit/SprintsHub/Common/TaskboardWorkItem";
import { ITaskboardRow } from "Wit/SprintsHub/SprintsView/Components/Taskboard/ITaskboardRow";
import { ITaskboardSignalRService } from "Wit/SprintsHub/SprintsView/Components/Taskboard/SignalR/TaskboardSignalRService";
import { TaskboardFilterDataSource } from "Wit/SprintsHub/SprintsView/Components/Taskboard/TaskboardFilterDataSource";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { ITaskboardService } from "Wit/SprintsHub/SprintsView/Services/ITaskboardService";
import { isClassificationPathEqualOrUnder, trimStartingBackslashes } from "Wit/SprintsHub/SprintsView/Utils/ClassificationPathUtils";
import { getCardStylesFromCardRules, getFieldEntryFromCardModel } from "Wit/SprintsHub/SprintsView/Utils/StyleUtils";
import { WorkItemTypeColorAndIcon } from "Wit/UI/Components/WorkItemTypeIcon";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { CoreLinkTypes } from "Wit/WorkItemModel/OM/WorkItemConstants";
import { IWorkItemPermissionData } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemEventData } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";

export interface IAdvanceBacklogManagementPermission {
    hasPermission: boolean;
}

enum DataProviders {
    backlogConfiguration = "ms.vss-work-web.agile-backlog-configuration-data-provider",
    taskboard = "ms.vss-work-web.new-sprints-hub-taskboard-data-provider",
    backlogPermission = "ms.vss-work-web.backlog-permission-data-provider",
    workItemTypeColorIconDataProvider = "ms.vss-work-web.work-item-type-color-icon-data-provider",
    workItemPermissionsDataProvider = "ms.vss-work-web.work-item-permissions-data-provider"
}

const parentIdField = "ParentId";

class TaskboardService extends ClientWorkItemService<IObservableClientWorkItem<ITaskboardWorkItem>> implements ITaskboardService {
    private allTaskboardRows = new ObservableArray<ITaskboardRow>();
    private filteredTaskboardRows = new ObservableArray<ITaskboardRow>();
    private backlogConfiguration: IBacklogConfiguration | undefined;
    private someRowsExpanded = new ObservableValue<boolean>(true);
    private readonly taskboardData = new ObservableValue<ISprintTaskboardData | undefined>(undefined);
    private readonly taskboardErrorData = new ObservableValue<ISprintTaskboardErrorData | undefined>(undefined);
    private taskboardRowMap = new Map<string, ITaskboardRow>();
    private workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[] | undefined;
    private planningService: IPlanningPaneViewService;
    private workItemModelService: IWorkItemModelService;
    private backlogContentService: ICommonBacklogContentService;

    private initializedTaskboardData: ISprintTaskboardData;
    private initializedTaskboardDataIterationId: string;

    private backlogPermissionData: IAdvanceBacklogManagementPermission | undefined;

    private workItemColumnIdMap = new ObservableValue<{
        [workItemId: number]: string;
    }>({});
    private columnIdToRemainingWorkMap = new Map<string, ObservableValue<number>>();
    private fieldEntry: IFieldEntry[] = [];

    private signalr: ITaskboardSignalRService;
    private teamId: string;

    private filter = new Filter();
    private filterManager: FilterManager;
    private filterDataSource: BoardFilterDataSource<IObservableClientWorkItem<ITaskboardWorkItem>>;
    private evaluatePeopleFilter: (rows?: ITaskboardRow[]) => boolean;

    public groupBy = new ObservableValue<TaskboardGroupBy>(TaskboardGroupBy.Loading);
    public selectedIteration: IIterationData;
    private hasPermissionToDeleteWorkItem = new ObservableValue<boolean>(true);

    public _serviceStart = (pageContext: IVssPageContext) => {
        super._serviceStart(pageContext);
        this.planningService = this.pageContext.getService<IPlanningPaneViewService>("IPlanningPaneViewService");
        this.signalr = this.pageContext.getService<ITaskboardSignalRService>("ITaskboardSignalRService");
        this.workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        this.backlogContentService = this.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.getPermissions();
    };

    public _serviceEnd = (pageContext: IVssPageContext) => {
        if (this.teamId) {
            this.signalr.endWatch(this.teamId);
        }

        this.clearChildSubscriptions();

        super._serviceEnd(pageContext);
    };

    public actualizeChildItem(child: IObservableClientWorkItem<ITaskboardWorkItem>, originalId: number) {
        child.subscribe(this.onChildItemChanged);
        this.get(child.value.id)!.value.id = child.value.id;
    }

    public createObservableWorkItem = (item: ITaskboardWorkItem) => {
        return new ObservableClientWorkItem(item);
    };

    public async createNewWorkItem(
        workItemType: string,
        title: string,
        workItem: IObservableClientWorkItem<ITaskboardWorkItem>,
        tempItemId: number
    ): Promise<void> {
        const projectId = getTFSProject(this.pageContext)?.id ?? "";
        const workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        const workItemTypeService = this.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");

        const workItemTypeDataPromise = workItemTypeService.getWorkItemTypeData(projectId, [workItemType]);
        let fieldEntryPromise: Promise<IFieldEntry[] | undefined>;
        if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)) {
            const fieldService = this.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
            fieldEntryPromise = fieldService.loadFields();
        } else {
            const fieldService = this.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
            fieldEntryPromise = fieldService.getFieldsAsync();
        }
        
        const [workItemTypeDataList, fieldEntries] = await Promise.all([workItemTypeDataPromise, fieldEntryPromise]);
        const workItemTypeData = workItemTypeDataList.length && workItemTypeDataList[0];
        if (!workItemTypeData) {
            console.error(`Failed to load work item type data for type ${workItemType}`);
            return;
        }

        const tempId = workItem.value.id;
        const workItemPromise = workItemModelService.createWorkItem(workItemTypeData, projectId, { tempId });
        workItem.value.setFieldValue(CoreFieldRefNames.Title, title);
        await workItem.value.hydrate(this.pageContext, workItemPromise);

        this._notify({ item: workItem.value }, ClientWorkItemServiceEvent.NewWorkItemAdded);

        const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        const teamSettings = await teamSettingsService.getTeamSettings();
        if (teamSettings) {
            workItem.value.setFieldValue(teamSettings.teamFieldName, teamSettings.teamFieldDefaultValue);
        }
        workItem.value.setFieldValue(CoreFieldRefNames.IterationId, this.selectedIteration.nodeId);

        workItem.value.saving = true;
        workItem.notify();

        this._afterNextSave.set(workItem.value, () => {
            this._items.delete(tempItemId);
            this._items.set(workItem.value.id, workItem);
            const original = this.workItemColumnIdMap.value[tempId];
            delete this.workItemColumnIdMap.value[tempId];
            this.workItemColumnIdMap.value[workItem.value.id] = original;
            // fire a second notify so listeners can react to full work item
            this._notify({ item: workItem.value }, ClientWorkItemServiceEvent.WorkItemUpdated);
        });

        if (await workItem.value.isValid(this.pageContext)) {
            await workItem.value.iWorkItem.value!.save();
            workItemModelService.addWorkItemToMap(workItem.value.iWorkItem.value!.id, workItem.value.iWorkItem.value!);
        }

        workItem.saveComplete();

        if (await workItem.value.isValid(this.pageContext)) {
            await workItem.value.iWorkItem.value!.save();
            await this.setOrderForNewItem(workItem.value, projectId);

            workItem.saveComplete();
        } else {
            const dialogService = this.pageContext.getService<IWorkItemFormDialogService>("IWorkItemFormDialogService");

            dialogService.showWorkItemFormDialog(
                workItem.value.iWorkItem.value!,
                () => {
                    workItem.saveComplete();
                },
                {
                    onDismiss: async () => {
                        if (workItem.value.id > 0) {
                            // item was saved
                            await this.setOrderForNewItem(workItem.value, projectId);
                            return;
                        }
                        // item was not saved, remove it
                        await this.removeWorkItem(workItem.value.id, { processChildItems: true });
                    }
                }
            );
        }
    }

    public getAllRows(): IObservableArray<ITaskboardRow> {
        return this.allTaskboardRows;
    }

    public getBacklogConfiguration = () => {
        if (!this.backlogConfiguration) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.backlogConfiguration = contributionService.getData<IBacklogConfiguration>(DataProviders.backlogConfiguration);
        }
        return this.backlogConfiguration;
    };

    public getFilter(): IFilter {
        return this.filter;
    }

    public getFilterManager(): FilterManager {
        return this.filterManager;
    }

    public getFilterSource(): BoardFilterDataSource<IObservableClientWorkItem<ITaskboardWorkItem>> {
        return this.filterDataSource;
    }

    public getFilteredRows = () => {
        if (!this.filteredTaskboardRows) {
            this.filteredTaskboardRows = new ObservableArray([]);
        }

        return this.filteredTaskboardRows;
    };

    public getInitializedTaskboardData(): ISprintTaskboardData {
        return this.initializedTaskboardData;
    }

    public getRemainingWorkMap(): Map<string, ObservableValue<number>> {
        return this.columnIdToRemainingWorkMap;
    }

    public getSomeRowsExpanded(): ObservableValue<boolean> {
        return this.someRowsExpanded;
    }

    public getTaskBoardData = () => {
        if (!this.taskboardData.value) {
            this.loadTaskboardData();
        }

        return this.taskboardData;
    };

    public getTaskBoardErrorData = () => {
        if (!this.taskboardData.value && !this.taskboardErrorData.value) {
            this.loadTaskboardData();
        }
        return this.taskboardErrorData;
    };

    private loadTaskboardData = () => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const taskboardData = contributionService.getData<ISprintTaskboardData | ISprintTaskboardErrorData>(DataProviders.taskboard);
        this.setTaskboardData(taskboardData);
    };

    public getTaskboardRowMap(): Map<string, ITaskboardRow> {
        return this.taskboardRowMap;
    }

    public getWorkItemColumnMap(): IObservableValue<{
        [workItemId: number]: string;
    }> {
        return this.workItemColumnIdMap;
    }

    public getWorkItemTypeColorAndIcons = () => {
        if (!this.workItemTypeColorAndIcons) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.workItemTypeColorAndIcons = contributionService.getData<WorkItemTypeColorAndIcon[]>(DataProviders.workItemTypeColorIconDataProvider);
        }

        return this.workItemTypeColorAndIcons;
    };

    public updateBackgroundColorForFilteredTaskboardRows = () => {
        const cardStyles = getCardStylesFromCardRules(this.taskboardData.value?.boardCardSettings.styles) ?? [];
        this.filteredTaskboardRows.value.map(row => {
            row.children.value.map(async child => {
                await child.updateBackgroundColor(this.pageContext, cardStyles);
            });
        });
    }

    public hasAgileToolsBacklogManagementPermission = (): boolean => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        if (!this.backlogPermissionData) {
            this.backlogPermissionData = contributionService.getData<IAdvanceBacklogManagementPermission>(DataProviders.backlogPermission);
        }

        return !!this.backlogPermissionData ? this.backlogPermissionData.hasPermission : false;
    };

    public initializeNewItem(fieldMap: INamedFieldDataDictionary, parentId?: number): IObservableClientWorkItem<ITaskboardWorkItem> {
        const workItem = new TaskboardWorkItem(fieldMap, this.fieldEntry);
        const observableWorkItem = this.add(workItem);

        if (parentId !== undefined) {
            const columnIdToWITStateMap = this.taskboardData.value?.taskboardModel.columnIdToWorkItemTypeStateMap;
            const workItemState = workItem.getFieldValue<string>(CoreFieldRefNames.State)!.toLocaleLowerCase();

            if (columnIdToWITStateMap) {
                const workItemType = workItem.getFieldValue<string>(CoreFieldRefNames.WorkItemType)!;

                this.workItemColumnIdMap.value[workItem.id] =
                    Object.keys(columnIdToWITStateMap).find(columnId => {
                        return columnIdToWITStateMap[columnId][workItemType]?.toLocaleLowerCase() === workItemState;
                    }) || workItemState;
            } else {
                this.workItemColumnIdMap.value[workItem.id] = workItemState;
            }

            if (this.taskboardData.value) {
                this.getRemainingWork(observableWorkItem, this.taskboardData.value.taskboardModel.workRollupField);
            }

            this.addItemToTaskboardMap(String(parentId), observableWorkItem, { addNewItemToTheTop: true, updateFilteredTaskboardRows: true });
        } else {
            // If a new unsaved parent, don't create entry in the for the parent yet in the taskboardRowMap
            const newRow = {
                children: new ObservableArray<IObservableClientWorkItem<ITaskboardWorkItem>>([]),
                expanded: new ObservableValue<boolean>(true),
                parent: observableWorkItem,
                rowType: TaskboardGroupBy.Parents
            };

            this.allTaskboardRows.splice(0, 0, newRow);
            this.filteredTaskboardRows.splice(0, 0, newRow);
        }
        this.addAssignedToRow(observableWorkItem);

        return observableWorkItem;
    }

    public hasRowData(): boolean {
        return this.allTaskboardRows.length > 0;
    }

    public initializeRows(ignoreCache?: boolean) {
        if (!this.taskboardData.value) {
            this.getTaskBoardData();
        }

        // rows are already initialized for the selected iteration
        const taskboardIterationId = this.taskboardData?.value?.iterationId;
        if (!ignoreCache && this.hasRowData() && this.initializedTaskboardDataIterationId === taskboardIterationId) {
            return;
        }

        const taskboardData = this.taskboardData.value;

        if (!taskboardData) {
            return;
        }

        this.initializedTaskboardData = taskboardData;

        const filterableFields = taskboardData.taskboardModel.filterableFieldNames ?? [];
        const backlogConfiguration = this.getBacklogConfiguration();
        this.filterDataSource = new TaskboardFilterDataSource(this, filterableFields, this.pageContext, backlogConfiguration);
        this.filterManager = new FilterManager(this.filterDataSource);
        this.clearChildSubscriptions();
        this.allTaskboardRows.removeAll();
        this.filteredTaskboardRows.removeAll();
        this.clearColumnIdToRemainingWorkMapValues();
        this.taskboardRowMap.clear();
        this.workItemColumnIdMap.value = taskboardData.taskboardModel.workItemColumnIdMap || {};
        if (taskboardData.taskboardModel.payload) {
            const workItems = taskboardData.taskboardModel.payload.data;
            const fieldRefNames = taskboardData.taskboardModel.payload.fieldRefNames;
            this.fieldEntry = getFieldEntryFromCardModel(taskboardData.taskboardModel.fieldDefinitions) ?? [];

            this.setTaskboardRowMap(taskboardData.taskboardModel, workItems, fieldRefNames);
            this.filteredTaskboardRows.value = this.allTaskboardRows.value.filter(
                row => (!row.parent || this.filterManager.itemMatchesFilters(row.parent.value.id)) && row.rowType === this.groupBy.value
            );

            const requirementBacklogId = backlogConfiguration?.requirementBacklog.id;
            if (requirementBacklogId) {
                // only need to fetch parent data for row parents
                this.populateWorkItemParents(taskboardData.taskboardModel.team.id, requirementBacklogId, this.getRowParentIds()).then(_ => {
                    const parentFilterItemState = this.filter.getFilterItemState(CoreFieldRefNames.Parent);
                    if (parentFilterItemState) {
                        // We need to update the current parent filter state due to parents of work items being populated
                        // after the main data is loaded and the filter is applied so we need to trigger it again if there is the parent filter
                        this.filter.setFilterItemState(CoreFieldRefNames.Parent, parentFilterItemState);
                    }
                });
            }
        }
        this.initializedTaskboardDataIterationId = taskboardData.iterationId;
    }

    public isStateComplete(workItem: ITaskboardWorkItem): boolean {
        if (!this.backlogConfiguration) {
            return false;
        }

        const stateValue = workItem.getFieldValue<string>(CoreFieldRefNames.State) as string;
        const workItemType = workItem.getFieldValue(CoreFieldRefNames.WorkItemType) as string;

        const workItemTypesService = this.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
        return workItemTypesService.checkCompletedState(stateValue, workItemType, this.backlogConfiguration);
    }

    public reloadTaskBoardData = async (): Promise<void> => {
        if (this.taskboardErrorData.value) {
            this.taskboardErrorData.value = undefined;
        }
        if (this.taskboardData.value) {
            this.taskboardData.value = undefined;
        }
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        this.backlogConfiguration = await contributionService.getDataAsync<IBacklogConfiguration>(
            DataProviders.backlogConfiguration,
            undefined,
            true
        );

        const taskboardData = await contributionService.getDataAsync<ISprintTaskboardData | ISprintTaskboardErrorData>(
            DataProviders.taskboard,
            undefined,
            /* ignoreCache */ true
        );
        this.setTaskboardData(taskboardData);
    };

    private setTaskboardData = (taskboardData: ISprintTaskboardData | ISprintTaskboardErrorData | undefined): void => {
        if (!taskboardData) {
            return;
        }
        if ("exceptionInfo" in taskboardData) {
            this.taskboardErrorData.value = taskboardData;
            this.taskboardData.value = undefined;
        } else if (!taskboardData.taskboardModel.isColumnMappingValid && taskboardData.taskboardModel.areColumnsCustomized) {
            const exceptionData: ISprintTaskboardErrorData = {
                exceptionInfo: {
                    exceptionMessage: taskboardData.taskboardModel.columnMappingsValidationMessage!,
                    additionalMessages: [Resources.ConfigurationToFix],
                    isSettingsException: true
                }
            };
            this.taskboardErrorData.value = exceptionData;
            this.taskboardData.value = taskboardData;
        } else {
            this.taskboardData.value = taskboardData;
            this.taskboardErrorData.value = undefined;
        }
    };

    public async removeWorkItem(workItemId: number, options?: { processChildItems?: boolean }): Promise<void> {
        const item = this.get(workItemId);
        // Remove assignedTo child
        const assignedToKey =
            convertWorkItemIdentityRefFromFieldValue(
                this.pageContext,
                item?.value.getFieldValue<string | WorkItemIdentityRef>(CoreFieldRefNames.AssignedTo)
            )?.identityRef.displayName ?? unassignedId;

        const assignedToRow = this.taskboardRowMap.get(assignedToKey);
        assignedToRow?.children.removeAll(child => child.value.id === item?.value.id);
        if (assignedToRow?.children.length === 0) {
            this.taskboardRowMap.delete(assignedToKey);
        }

        if (item) {
            let parentId = this.getParentIdWithBacklogCheck(this.getParentId(item.value) ?? 0);
            // Current workItemId is parent
            if (this.taskboardRowMap.has(String(workItemId))) {
                // If there are children - parent should exists until removing all children - just mark it
                item.value.queuedForRemoving.value = true;
                parentId = workItemId;
            } else {
                // If it is child - first step - remove from columnMap
                if (this.workItemColumnIdMap.value) {
                    delete this.workItemColumnIdMap.value[workItemId];
                    // We need to force reloading of parent work item the next time it is opened to have an up to date list of links
                    if (parentId) {
                        this.workItemModelService.clearWorkItem(parentId);
                    }
                }
            }
            // Remove workitem card from board
            this.removeItemFromTaskboardMap(parentId, workItemId);
        }

        if (this.planningService.planningItemProvider) {
            this.planningService.updateIterations();
        }
        this.clear(workItemId);

        return this.hydrateAndRefreshChildWorkItems(workItemId, options);
    }

    // if we deleted parent WorkItem, then we should update and rerender all children items
    private async hydrateAndRefreshChildWorkItems(workItemId: number, options?: { processChildItems?: boolean }): Promise<void> {
        const rowKey = String(workItemId);
        if (!options?.processChildItems || !this.taskboardRowMap.has(rowKey)) {
            return; // exit early if options or row are falsy
        }
        const row = this.taskboardRowMap.get(rowKey);
        const children = row?.children?.value;

        if (!children) {
            return; // exit early if no children
        }
        for (const childWorkItem of children) {
            const iWorkItem = childWorkItem?.value?.iWorkItem;
            if (!iWorkItem?.value && childWorkItem?.value?.id >= 0) {
                await childWorkItem.value.hydrate(this.pageContext);
            }

            if (iWorkItem?.value) {
                await iWorkItem.value.refresh(); // hydrate have cashing
                this.workItemChangedCallback({ workItem: iWorkItem.value });
            }
        }
    }

    public setIteration(iteration: IIterationData) {
        const prevSelectedIteration = this.selectedIteration;
        this.selectedIteration = iteration;

        if (!prevSelectedIteration) {
            // don't reload if we're initially setting the iteration, the data from the data provider should be correct.
            return;
        }

        this.reloadTaskBoardData();
    }

    public setEvaluatePeopleFilter(evaluatePeopleFilter: (rows?: ITaskboardRow[]) => boolean) {
        this.evaluatePeopleFilter = evaluatePeopleFilter;
    }

    public setSomeRowsExpanded(expanded: boolean) {
        this.someRowsExpanded.value = expanded;
    }

    public async updateItem(itemId: number, fieldReferenceName: string, newValue: any): Promise<string | undefined> {
        const observableItem = this.get(itemId);

        if (fieldReferenceName === CoreFieldRefNames.IterationId && newValue !== this.selectedIteration.nodeId) {
            await this.removeWorkItem(itemId);
        }

        if (!observableItem) {
            console.error(`Could not find observable item ${itemId}`);
            return;
        }

        try {
            observableItem.value.setFieldValue(fieldReferenceName, newValue);
            observableItem.notify();
            observableItem.value.isTitleEditing.value = false;
            if (await observableItem.value.isValid(this.pageContext)) {
                await observableItem.value.save(this.pageContext);
            }
        } catch (err) {
            console.error("Error updating item: %O", err);
            return err.toString();
        }
    }

    public async updateIteration(
        currentIterationData: ICurrentIterationData,
        currentItem: ITaskboardWorkItem,
        successCallback?: (workItemDTO: IChangeIterationWorkItemData[], numberOfSavedWorkitems: number) => Promise<void>, 
        useChangeIterationPerfomanceOptimization?: boolean
    ): Promise<void> {
        const workItemRow = this.getTaskboardRowMap().get(currentItem.id.toString());
        const workItems: ITaskboardWorkItem[] = [currentItem];

        if (workItemRow) {
            const childItems = workItemRow.children.value.map(child => child.value);
            const filteredByState = childItems.filter(child => !this.isStateComplete(child));

            workItems.push(...filteredByState);
        }

        const workItemDTO: IChangeIterationWorkItemData[] = workItems.map(workItem => {
            return {
                workItemId: workItem.id,
                workItemTypeName: workItem.getFieldValue(CoreFieldRefNames.WorkItemType) as string,
                workItemIterationPath: workItem.getFieldValue(CoreFieldRefNames.IterationPath) as string,
                workItemIterationId: workItem.getFieldValue(CoreFieldRefNames.IterationId) as number,
                workItemProjectName: workItem.getFieldValue(CoreFieldRefNames.TeamProject) as string,
            };
        });

        const updateResult = await this.backlogContentService.updateIterationCommonLogic(
            currentIterationData, 
            workItemDTO, 
            this.filterManager, 
            undefined, 
            useChangeIterationPerfomanceOptimization,
            true);

        if (useChangeIterationPerfomanceOptimization && currentIterationData.newNodeId !== this.selectedIteration.nodeId) {
            for (let i = workItems.length - 1; i >= 0; i--) {
                const updatedWorkItem = workItems[i];
                await this.removeWorkItem(updatedWorkItem.id);
            }
        }
        if (updateResult.changeIterationIsSuccessful) {
            await successCallback?.(workItemDTO, updateResult.numberOfSavedWorkitems);
        }
    }

    public watchTeamTaskboard(teamId: string) {
        if (teamId !== this.teamId) {
            this.signalr.endWatch(this.teamId);
            this.signalr.watch(
                teamId,
                this.takeWorkItemsSignalRUpdate,
                this.takeWorkItemColumnSignalRUpdate,
                this.reloadTaskBoardData,
                this.reloadTaskBoardData,
                this.updateTeamSettings
            );
            this.teamId = teamId;
        }
    }

    public workItemChangedCallback = async ({ workItem }: IWorkItemEventData) => {
        const item = this.get(workItem.id);
        if (item) {
            const payload = {
                id: workItem.id,
                changeType: WorkItemSignalREventType.ItemUpdated,
                revision: workItem.revision.value,
                stackRank: workItem.getFieldValueByName(OobFieldRefNames.StackRank),
                iterationPath: workItem.getFieldValueByName(CoreFieldRefNames.IterationPath),
                parentId: this.getParentIdWithBacklogCheck(
                    workItem.getLinks().find(link => link.linkData.LinkType === CoreLinkTypes.Parent)?.linkData.ID ?? 0
                ),
                parentChangeType: ""
            };
            await this.onWorkItemUpdated(payload, item);
        }
    };

    private clearColumnIdToRemainingWorkMapValues = () => {
        this.columnIdToRemainingWorkMap.forEach(value => (value.value = 0));
    };

    public resetColumnIdToRemainingWorkMapValues(): void {
        if (!this.filteredTaskboardRows.value || this.filteredTaskboardRows.value.length === 0) {
            return;
        }

        if (!this.taskboardData.value) {
            return;
        }

        this.clearColumnIdToRemainingWorkMapValues();
        this._items.forEach(workItem => {
            if (this.needToSkipThisChildTaskForRemainingHoursCalculation(workItem)) {
                return;
            }

            this.getRemainingWork(workItem, this.taskboardData.value!.taskboardModel.workRollupField);
        });
    }

    public hasPermissionToDeleteWorkItems(): ObservableValue<boolean> {
        return this.hasPermissionToDeleteWorkItem;
    }

    private async getPermissions() {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const workItemPermissions = await contributionService.getDataAsync<IWorkItemPermissionData>(DataProviders.workItemPermissionsDataProvider);
        this.hasPermissionToDeleteWorkItem.value = workItemPermissions?.workItemDeletePermission?.hasPermission ?? false;
    }

    private async setOrderForNewItem(workItem: ITaskboardWorkItem, projectId: string): Promise<void> {
        if (
            !this.backlogConfiguration?.requirementBacklog.workItemTypes.some(wit => wit === workItem.getFieldValue(CoreFieldRefNames.WorkItemType))
        ) {
            return;
        }
        const firstExistingWorkItem = this.filteredTaskboardRows.value.find(row => row.parent?.value.id && row.parent.value.id !== workItem.id)
            ?.parent!.value;

        firstExistingWorkItem &&
            (await this.reorderWorkItems(
                workItem.id,
                projectId,
                this.teamId,
                0, // previousId
                firstExistingWorkItem.id, // nextId
                this.selectedIteration.friendlyPath,
                undefined, // options
                undefined, // orderFieldName
                undefined, // updateStackRank
                this.selectedIteration.id
            ));
    }

    private addAssignedToRow(workItem: IObservableClientWorkItem<ITaskboardWorkItem>) {
        const assignedTo: IdentityRef = convertWorkItemIdentityRefFromFieldValue(
            this.pageContext,
            workItem.value.getFieldValue<string | WorkItemIdentityRef>(CoreFieldRefNames.AssignedTo)
        )?.identityRef;

        if (
            this.taskboardData.value?.taskboardModel.childWorkItemTypes.some(
                type => type.toLowerCase() === (workItem.value.getFieldValue(CoreFieldRefNames.WorkItemType) as string).toLowerCase()
            )
        ) {
            this.addItemToTaskboardMap(assignedTo?.displayName ?? unassignedId, workItem, {
                parent: false,
                assignedTo,
                addNewItemToTheTop: true,
                updateFilteredTaskboardRows: true
            });
        }
    }

    private addItemToTaskboardMap(
        key: string,
        workItem: IObservableClientWorkItem<ITaskboardWorkItem>,
        options?: { parent?: boolean; assignedTo?: IdentityRef; addNewItemToTheTop?: boolean; updateFilteredTaskboardRows?: boolean }
    ) {
        // check for unassigned item
        const existedRow = this.taskboardRowMap.get(key);
        if (existedRow && !options?.parent) {
            if (!existedRow.children.value.some(child => child.value.id === workItem.value.id)) {
                existedRow.children.push(workItem);
            }
        } else {
            const newRow = {
                assignedTo: options?.assignedTo,
                children: new ObservableArray<IObservableClientWorkItem<ITaskboardWorkItem>>(options?.parent ? [] : [workItem]),
                expanded: new ObservableValue<boolean>(options?.assignedTo ? true : !this.isStateComplete(workItem.value)),
                parent: options?.parent ? workItem : undefined,
                rowType: !options?.assignedTo ? TaskboardGroupBy.Parents : TaskboardGroupBy.AssignedTo
            };
            this.taskboardRowMap.set(key, newRow);

            if (options?.addNewItemToTheTop) {
                this.allTaskboardRows.splice(0, 0, newRow);
            } else {
                if (!newRow.parent) {
                    //unparanted item should be on the top
                    this.allTaskboardRows.splice(0, 0, newRow);
                } else {
                    this.allTaskboardRows.push(newRow);
                }
            }

            if (options?.updateFilteredTaskboardRows) {
                if (this.filterManager.itemMatchesFilters(workItem.value.id) && newRow.rowType === this.groupBy.value) {
                    this.filteredTaskboardRows.splice(0, 0, newRow);
                }
            }
        }
    }

    private removeItemFromTaskboardMap(key: number, workItemId: number): void {
        const row = this.taskboardRowMap.get(String(key));
        row?.children?.removeAll(child => child.value.id === workItemId);

        // After removing workItem - we should check whether it is necessary to remove row
        this.checkRowForRemoving(key ?? workItemId);
    }

    private checkRowForRemoving(parentWorkItemId: number): void {
        const row = this.taskboardRowMap.get(String(parentWorkItemId));
        if (!row) {
            return;
        }
        const children = row.children?.value;
        const iterationId = row.parent?.value.getFieldValue<number>(CoreFieldRefNames.IterationId);
        // Check if parent doesn't have any children AND
        // If parent iteration differs from current iteration (was just fake parent for proper view) OR
        // If parent queuedForRemoving = changing iteration
        if (!children?.length && (row.parent?.value.queuedForRemoving.value || this.selectedIteration.nodeId !== iterationId)) {
            if (this.workItemColumnIdMap.value) {
                delete this.workItemColumnIdMap.value[parentWorkItemId];
            }
            this.taskboardRowMap.delete(String(parentWorkItemId));
            // Process unparented and parented rows
            if (!row.parent) {
                this.allTaskboardRows.removeAll(row => row.rowType === TaskboardGroupBy.Parents && !row.parent);
                this.filteredTaskboardRows.removeAll(row => row.rowType === TaskboardGroupBy.Parents && !row.parent);
                return;
            }
            this.allTaskboardRows.removeAll(row => row.parent?.value.id === parentWorkItemId);
            this.filteredTaskboardRows.removeAll(row => row.parent?.value.id === parentWorkItemId);
            row.parent.value.queuedForRemoving.value = false;
        }
    }

    private clearChildSubscriptions = () => {
        this.allTaskboardRows.value.forEach(taskboardRow => {
            taskboardRow.children.value.forEach(child => {
                child.unsubscribe(this.onChildItemChanged);
            });
        });
    };

    private createFieldMap = (data: any[], fieldRefNames: string[], parentIds?: Set<number>) => {
        const fieldMap: INamedFieldDataDictionary = {};
        fieldRefNames.forEach((name, index) => {
            fieldMap[name] = data[index];
        });

        if (fieldMap[parentIdField] === 0) {
            return fieldMap;
        }

        const parentId: number | undefined = fieldMap[parentIdField];

        if (parentId && parentIds?.has(parentId) && this._items.has(parentId)) {
            const parentItem = this._items.get(parentId)!.value;

            fieldMap[CoreFieldRefNames.Parent] = parentItem.id;
            fieldMap[ParentField.Title] = parentItem.getFieldValue(CoreFieldRefNames.Title);
            fieldMap[ParentField.WorkItemType] = parentItem.getFieldValue(CoreFieldRefNames.WorkItemType);
        }

        return fieldMap;
    };

    private getParentId(workItem: IClientWorkItem) {
        let parentId = workItem.getFieldValue<number>(parentIdField);
        if (parentId === undefined) {
            parentId = workItem.getFieldValue<number>(CoreFieldRefNames.Parent);
        }
        return parentId;
    }

    private getRemainingWork(child: IObservableClientWorkItem, workRollupField: string) {
        let childColumn = this.workItemColumnIdMap.value[child.value.id];
        if (!childColumn) {
            const childState = child.value.getFieldValue<string>(CoreFieldRefNames.State)!.toLocaleLowerCase();
            this.workItemColumnIdMap.value[child.value.id] = childState;
            childColumn = childState;
        }

        const childRemainingWork = child.value.getFieldValue<number>(workRollupField);
        if (childRemainingWork) {
            if (this.columnIdToRemainingWorkMap.has(childColumn)) {
                this.columnIdToRemainingWorkMap.get(childColumn)!.value += childRemainingWork;
            } else {
                this.columnIdToRemainingWorkMap.set(childColumn, new ObservableValue<number>(childRemainingWork));
            }
        }
    }

    private moveChildToParentsRow = (workItemId: number, newParentRow: ITaskboardRow | undefined) => {
        if (!newParentRow) {
            return;
        }
        const parentAlreadyContainsItem = newParentRow.children.value.some(child => child.value.id === workItemId);
        if (!parentAlreadyContainsItem) {
            const child = this.get(workItemId);
            if (child) {
                this.taskboardRowMap.forEach((row, key) => {
                    if (key !== unassignedId) {
                        row.children.removeAll(existingChild => existingChild.value.id === child.value.id);
                    }
                });
                newParentRow.children.value.push(child);
            }
        }
    };

    private onChildItemChanged = (value: IClientWorkItem, action: WorkItemEvents) => {
        if (action === WorkItemEvents.ComputedStyleUpdated) {
            return;
        }

        // find the row the child is in and update the children
        const parentId = this.getParentIdWithBacklogCheck(value.getFieldValue<number>(CoreFieldRefNames.Parent) ?? 0);
        const parentRow = this.taskboardRowMap.get(String(parentId));
        if (parentRow) {
            parentRow.children.value = parentRow.children.value;
        }
        if (action === WorkItemEvents.Set) {
            if (parentRow) {
                this.moveChildToParentsRow(value.id, parentRow);
            } else {
                let unparentedRow = this.taskboardRowMap.get(String(unparentedId));
                if (!unparentedRow) {
                    unparentedRow = {
                        children: new ObservableArray<IObservableClientWorkItem<ITaskboardWorkItem>>([]),
                        expanded: new ObservableValue<boolean>(true),
                        rowType: TaskboardGroupBy.Parents
                    };
                    this.taskboardRowMap.set(String(unparentedId), unparentedRow);
                    this.allTaskboardRows.splice(0, 0, unparentedRow);
                    if (this.filterManager.itemMatchesFilters(value.id)) {
                        this.filteredTaskboardRows.splice(0, 0, unparentedRow);
                    }
                } else {
                    unparentedRow.children.value = unparentedRow.children.value;
                }
                this.moveChildToParentsRow(value.id, unparentedRow);
            }
        }

        if (this.taskboardData.value?.taskboardModel.workRollupField) {
            // find the column that the child is in
            const childId = value.id;
            const childColumn = this.workItemColumnIdMap.value[childId];
            // recalculate the remaining work for that column
            let remainingWork = 0;
            for (const [workItemId, columnId] of Object.entries(this.workItemColumnIdMap.value)) {
                if (columnId === childColumn) {
                    const child = this.get(parseInt(workItemId));
                    if (child) {
                        if (this.needToSkipThisChildTaskForRemainingHoursCalculation(child)) {
                            continue;
                        }

                        const childRemainingWork = child.value.getFieldValue<string>(this.taskboardData.value.taskboardModel.workRollupField);
                        if (childRemainingWork) {
                            remainingWork += parseFloat(childRemainingWork);
                        }
                    }
                }
            }
            const remainingWorkObservable = this.columnIdToRemainingWorkMap.get(childColumn);
            if (remainingWorkObservable) {
                remainingWorkObservable.value = remainingWork;
            }
        }
    };

    public needToSkipThisChildTaskForRemainingHoursCalculation(child: IObservableClientWorkItem<ITaskboardWorkItem>): boolean {
        if (!this.filteredTaskboardRows.value?.length) {
            return false;
        }

        const notTaskOrFilteredOut =
            child.value.filteredOut.value ||
            !this.taskboardData.value?.taskboardModel.childWorkItemTypes.some(t => t === child.value.getFieldValue(CoreFieldRefNames.WorkItemType));

        if (notTaskOrFilteredOut) {
            return true;
        }

        if (this.groupBy.value === TaskboardGroupBy.AssignedTo) {
            const assignedToValue = child.value.getFieldValue<string | IdentityRef>(CoreFieldRefNames.AssignedTo);
            if (!assignedToValue) {
                // child is unassigned and unassigned row is hidden by filter
                return !this.filteredTaskboardRows.value.some(row => !row.assignedTo?.displayName);
            }

            return this.isAssignedToRowFilteredOut(assignedToValue);
        }

        const parentRowIsHiddenByFilter =
            child.value.parentId.value > 0 && !this.filteredTaskboardRows.value.some(row => row.parent?.value.id === child.value.parentId.value);

        const childIsUnparentedAndUnparentedRowIsHiddenByFilter =
            child.value.parentId.value <= 0 && !this.filteredTaskboardRows.value.some(row => !row.parent);

        return parentRowIsHiddenByFilter || childIsUnparentedAndUnparentedRowIsHiddenByFilter;
    }

    private isAssignedToRowFilteredOut(assignedToValue: string | IdentityRef): boolean {
        if (typeof assignedToValue === "string") {
            return !this.filteredTaskboardRows.value.some(
                row => row.assignedTo && getDistinctDisplayName(row.assignedTo.displayName, row.assignedTo.uniqueName) === assignedToValue
            );
        }

        return !this.filteredTaskboardRows.value.some(row => row.assignedTo && row.assignedTo.id === assignedToValue.id);
    }

    private async onWorkItemUpdated(
        payload: ITaskboardWorkItemChangedPayload,
        updatedItem: IObservableClientWorkItem<ITaskboardWorkItem>,
        forceRefresh?: boolean
    ) {
        // normalize iteration path by removing starting backslashes
        if (payload.iterationPath.startsWith("\\")) {
            payload.iterationPath = trimStartingBackslashes(payload.iterationPath);
        }

        const isParentWithChildren = !!this.taskboardRowMap.get(String(payload.id))?.children.length;
        if (!isParentWithChildren && !isClassificationPathEqualOrUnder(payload.iterationPath, this.selectedIteration.friendlyPath, true)) {
            await this.removeWorkItem(updatedItem.value.id);
            return;
        }

        if (updatedItem.value.id >= 0) {
            await updatedItem.value.hydrate(this.pageContext);
            if (forceRefresh && updatedItem.value.iWorkItem.value) {
                await updatedItem.value.iWorkItem.value.refresh({ doNotResetIfManuallyDirty: true });
            }
        }

        const newParentId = this.getParentIdWithBacklogCheck(
            (payload.parentChangeType == ParentChangeType.Unchanged ? this.getParentId(updatedItem.value) : payload.parentId) ?? 0
        );
        const oldParentId = updatedItem.value.parentId.value ?? 0;
        if (newParentId !== oldParentId) {
            this.removeItemFromTaskboardMap(oldParentId, updatedItem.value.id);

            const workItemType = updatedItem.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType);

            const parentIsAlreadyExistedOnBoard = this.taskboardRowMap.has(String(newParentId));

            const currentTypeMatchesToParentTypes =
                workItemType && this.taskboardData?.value?.newWorkItemTypes?.some(typeName => equals(typeName, workItemType, true));
            const currentTypeMatchesToChildTypes =
                workItemType && this.taskboardData?.value?.taskboardModel?.childWorkItemTypes?.some(typeName => equals(typeName, workItemType, true));

            // do not add Card of currentItem to the TaskBoard
            // - if current workItem type is not matches to allowed Types
            // - or if current workItem type is belongs to parentTypes and newParent is not present on the board
            // for example
            // - if currentItem is Task we should try to add it to the Board
            // - if currentItem is UserStory, then we should add it to the Board only if its parent workItem (Feature, UserStory) already exists on Board
            const currentTypeCheck = currentTypeMatchesToChildTypes || (currentTypeMatchesToParentTypes && parentIsAlreadyExistedOnBoard);

            if (currentTypeCheck) {
                const newParent = this.get(newParentId);
                if (newParent && !updatedItem.value.iWorkItem.value?.saving.value) {
                    //signalRUpdate handling
                    updatedItem.value.setFieldValue(ParentField.WorkItemType, newParent.value.getFieldValue(CoreFieldRefNames.WorkItemType));
                    updatedItem.value.setFieldValue(ParentField.Title, newParent.value.getFieldValue(CoreFieldRefNames.Title));
                }

                this.addItemToTaskboardMap(String(newParentId), updatedItem, {
                    parent: false,
                    addNewItemToTheTop: true,
                    updateFilteredTaskboardRows: true
                });
            }
            updatedItem.value.parentId.value = newParentId ?? 0;
        }

        const oldState = updatedItem.value.state.value;
        const state = updatedItem.value.getFieldValue<string>(CoreFieldRefNames.State)!.toLocaleLowerCase();
        if (oldState !== state) {
            updatedItem.value.state.value = state;
        }

        updatedItem.notify();

        const areColumnsCustomized = this.taskboardData.value?.taskboardModel.areColumnsCustomized;
        if (this.workItemColumnIdMap.value && this.workItemColumnIdMap.value[payload.id] && this.workItemColumnIdMap.value[payload.id] !== state) {
            if (areColumnsCustomized) {
                const columnIdToWITStateMap = this.taskboardData.value?.taskboardModel.columnIdToWorkItemTypeStateMap!;
                const workItemType = updatedItem.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType)!;
                const currentColumnId = this.workItemColumnIdMap.value[payload.id];

                // update WI column mapping if state is not proper for current column (scenario of state updating)
                // it prevents cards moving back and forth after drag'n'drop.
                if (columnIdToWITStateMap[currentColumnId][workItemType]?.toLocaleLowerCase() !== state.toLocaleLowerCase()) {
                    this.workItemColumnIdMap.value[payload.id] =
                        Object.keys(columnIdToWITStateMap).find(columnId => {
                            return columnIdToWITStateMap[columnId][workItemType]?.toLocaleLowerCase() === state.toLocaleLowerCase();
                        }) || state;
                }
            } else {
                this.workItemColumnIdMap.value[payload.id] = state;
            }
            this.workItemColumnIdMap.notify(this.workItemColumnIdMap.value, "update");
        }

        const filterFunction = (child: IObservableClientWorkItem<ITaskboardWorkItem>) => child.value.id === updatedItem.value.id;
        const assignedToRows = this.allTaskboardRows.value.filter(row => row.rowType === TaskboardGroupBy.AssignedTo);
        const currentRow = assignedToRows.find(row => row.children.value.find(filterFunction));
        const assignedTo = updatedItem.value.getFieldValue<IdentityRef>(CoreFieldRefNames.AssignedTo, undefined, undefined, true);
        if (currentRow && currentRow.assignedTo?.id !== assignedTo?.id) {
            currentRow.children.removeAll(filterFunction);
            this.addAssignedToRow(updatedItem);
        }

        // Evaluate people filter only within potentially changed rows
        this.evaluatePeopleFilter?.(assignedToRows);
    }

    private getParentIdWithBacklogCheck(parentId: number): number {
        return parentId > 0 &&
            !!this.backlogConfiguration?.requirementBacklog.workItemTypes.some(
                wit => wit === this.get(parentId)?.value.getFieldValue(CoreFieldRefNames.WorkItemType)
            )
            ? parentId
            : 0;
    }

    private setTaskboardRowMap(taskboardModel: ITaskboardModelOptions, workItems: { [key: number]: (string | number)[] }, fieldRefNames: string[]) {
        const childItems: IObservableClientWorkItem<ITaskboardWorkItem>[] = [];
        let wasNewItemAdded: boolean = false;

        const taskboardWorkItems: IObservableClientWorkItem<ITaskboardWorkItem>[] = [];
        const parentIds = new Set(taskboardModel.parentIds);

        for (const [key, value] of Object.entries(workItems)) {
            if (!this.taskboardRowMap.has(key)) {
                const workItem = new TaskboardWorkItem(this.createFieldMap(value, fieldRefNames, parentIds), this.fieldEntry);
                wasNewItemAdded = wasNewItemAdded || !this._items.get(workItem.id);
                const workItemObservable = this.add(workItem);
                taskboardWorkItems.push(workItemObservable);
            }
        }

        const compositeComparer = new CompositeComparer<IObservableClientWorkItem>();
        compositeComparer.push(new BaseIntComparer(SortDirection.Ascending, item => item?.value?.getFieldValue(taskboardModel.orderByField)));
        compositeComparer.push(new BaseIntComparer(SortDirection.Ascending, item => item?.value.id));
        const workItemSorter = compositeComparer.getComparer();
        taskboardWorkItems.sort(workItemSorter);

        for (const workItemObservable of taskboardWorkItems) {
            // Parent item
            if (taskboardModel.parentIds.some(parentId => parentId === workItemObservable.value.id)) {
                this.addItemToTaskboardMap(String(workItemObservable.value.id), workItemObservable, { parent: true });
            } else if (
                taskboardModel.childWorkItemTypes.some(
                    type =>
                        type.toLowerCase() === (workItemObservable.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType) ?? "").toLowerCase()
                )
            ) {
                workItemObservable.subscribe(this.onChildItemChanged);
                this.getRemainingWork(workItemObservable, taskboardModel.workRollupField);

                // defer adding children until all parents are added
                childItems.push(workItemObservable);
            }

            this.addAssignedToRow(workItemObservable);
        }

        for (const workItemObservable of childItems) {
            // The dataProvider gives the parent as ParentId instead of System.Parent
            const parentId = workItemObservable.value.getFieldValue<number>(parentIdField);
            if (parentId !== undefined && parentId !== null && this.taskboardRowMap.has(String(parentId))) {
                this.addItemToTaskboardMap(String(parentId), workItemObservable);
            } else {
                this.addItemToTaskboardMap(String(unparentedId), workItemObservable);
            }
        }

        if (wasNewItemAdded) {
            this._notify(workItems, ClientWorkItemServiceEvent.NewWorkItemAdded);
        }
    }

    private takeWorkItemColumnSignalRUpdate = (updatePayloads: ITaskboardWorkItemColumnChangedPayload[]) => {
        updatePayloads.forEach(payload => {
            this.workItemColumnIdMap.value[payload.workItemId] = payload.columnId;
        });
        this.workItemColumnIdMap.notify(this.workItemColumnIdMap.value, "update");
    };

    private takeWorkItemsSignalRUpdate = async (updatePayloads: ITaskboardWorkItemChangedPayload[]): Promise<void> => {
        updatePayloads.forEach(async payload => {
            switch (payload.changeType) {
                case WorkItemSignalREventType.ItemAdded:
                case WorkItemSignalREventType.ItemCreated:
                case WorkItemSignalREventType.ItemRestored:
                    // If not on the board return or already there don't create
                    if (payload.iterationPath.startsWith("\\")) {
                        payload.iterationPath = payload.iterationPath.slice(2);
                    }

                    if (
                        !this.selectedIteration ||
                        !isClassificationPathEqualOrUnder(payload.iterationPath, this.selectedIteration.friendlyPath, true) ||
                        this.get(payload.id)
                    ) {
                        return;
                    }
                    const fieldMap: INamedFieldDataDictionary = { [CoreFieldRefNames.Id]: payload.id };
                    const newItem = new TaskboardWorkItem(fieldMap);
                    if (newItem.id >= 0) {
                        await newItem.hydrate(this.pageContext);
                    }
                    const newItemObservable = this.add(newItem);

                    const filterMatch = this.filterManager.itemMatchesFilters(newItem.id);

                    let parentId = this.getParentId(newItem);
                    // Check if a parent item or an unparented child
                    if (parentId === undefined) {
                        const type = newItem.getFieldValue<string>(CoreFieldRefNames.WorkItemType);
                        // If it is a child type then it is an unparented item
                        if (
                            type &&
                            this.taskboardData.value &&
                            this.taskboardData.value.taskboardModel.childWorkItemTypes.some(childType => equals(type, childType))
                        ) {
                            parentId = unparentedId;
                        }
                    }

                    if (this.taskboardData.value) {
                        this.getRemainingWork(newItemObservable, this.taskboardData.value.taskboardModel.workRollupField);
                    }

                    this.addAssignedToRow(newItemObservable);

                    if (parentId !== undefined) {
                        const state = newItem.getFieldValue<string>(CoreFieldRefNames.State)!.toLocaleLowerCase();
                        if (state && this.workItemColumnIdMap.value) {
                            this.workItemColumnIdMap.value[payload.id] = state;
                        }
                        newItem.filteredOut.value = !filterMatch;
                        this.addItemToTaskboardMap(String(parentId), newItemObservable);
                    } else {
                        const newRow: ITaskboardRow = {
                            parent: this.add(newItem),
                            children: new ObservableArray<IObservableClientWorkItem<ITaskboardWorkItem>>([]),
                            expanded: new ObservableValue<boolean>(!this.isStateComplete(newItem)),
                            rowType: TaskboardGroupBy.Parents
                        };
                        this.taskboardRowMap.set(String(newItem.id), newRow);
                        this.allTaskboardRows.push(newRow);
                        if (filterMatch && newRow.rowType === this.groupBy.value) {
                            this.filteredTaskboardRows.push(newRow);
                        }
                    }

                    if (this.planningService.planningItemProvider) {
                        this.planningService.updateIterations();
                    }
                    break;
                case WorkItemSignalREventType.ItemUpdated:
                    const updatedItem = this.get(payload.id);
                    if (updatedItem) {
                        const underlyingItemRev = updatedItem.value.iWorkItem.value?.getFieldValueByName(CoreFieldRefNames.Rev);
                        const parentIdDiffers = payload.parentId !== updatedItem.value.iWorkItem.value?.getFieldValueByName(CoreFieldRefNames.Parent);

                        // This will be true only for SignalR events not induced by manual action on the current page.
                        // In case of manual action a WI will be refreshed in onWorkItemFormSaveComplete callback.
                        const forceRefresh =
                            !underlyingItemRev ||
                            underlyingItemRev !== payload.revision ||
                            (payload.parentChangeType !== ParentChangeType.Unchanged && parentIdDiffers);
                        this.onWorkItemUpdated(payload, updatedItem, forceRefresh);
                    }
                    break;
                case WorkItemSignalREventType.ItemDeleted:
                case WorkItemSignalREventType.ItemRemoved:
                    await this.removeWorkItem(payload.id);
                    break;
                default:
                    break;
            }
        });
    };

    private updateTeamSettings = () => {
        const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        teamSettingsService.getTeamSettingsObservable({ ignoreCache: true });
    };

    private getRowParentIds() {
        return Array.from(this.taskboardRowMap.values())
            .filter(row => row.parent)
            .map(row => row.parent!.value.id);
    }
}

Services.add("ITaskboardService", { serviceFactory: TaskboardService });
